/**
 * Theme management. The initial class is set by the inline boot script in
 * index.html (before CSS loads, prevents dark→light flash), which reads the
 * `theme` key below - so that key stays written even though the choice is also
 * a stored preference now. The boot script cannot wait for `/api/me`, and a
 * flash of the wrong theme is exactly what it exists to prevent.
 *
 * On a browser this account has never used, the stored choice arrives with
 * `/api/me` and `applyStoredTheme` switches once, early.
 *
 * NOTE (parity): the blob also calls `rerenderChartFromLast()` on toggle so
 * uPlot picks up new CSS-var colors. Wire that in when the plot is ported.
 */

import { rememberPreference } from '../state/preferences.ts';

export type Theme = 'light' | 'dark';

export function getTheme(): Theme {
  return document.documentElement.classList.contains('light') ? 'light' : 'dark';
}

export function setTheme(theme: Theme): void {
  applyTheme(theme);
  rememberPreference({ theme });
}

/** Paint a theme without recording it - the path a stored choice arrives on. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle('light', theme === 'light');
  try {
    localStorage.setItem('theme', theme);
  } catch {
    /* private mode - the class is still set for this session */
  }
}

/** Adopt a theme that came from the account, if it differs from what is shown. */
export function applyStoredTheme(theme: Theme | null | undefined): void {
  if ((theme === 'light' || theme === 'dark') && theme !== getTheme()) applyTheme(theme);
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'light' ? 'dark' : 'light';
  setTheme(next);
  return next;
}
